import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ArrowUpRight, GitBranch, Maximize2 } from "lucide-react";
import { z } from "zod";

import {
  useDecisionsList,
  useUpdateDecision,
} from "@/application/use-cases/decisions/use-decisions.queries";
import { DecisionDrawerContent } from "@/presentation/components/decisions/decision-drawer-content";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { StatusDot, STATUS_LABEL } from "@/presentation/components/status/status-dot";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@monorepo-apps/ui/src/components/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@monorepo-apps/ui/src/components/sheet";
import { cn } from "@monorepo-apps/ui/src/lib/utils";
import type { DecisionRow, Status } from "@/infrastructure/supabase/database.types";

const STATUS_ORDER: Status[] = ["active", "open", "resolved", "blocked"];

const GROUP_TONE: Record<Status, { band: string; ring: string; mute: string; emptyHint: string }> =
  {
    active: {
      band: "bg-[oklch(0.74_0.16_80_/_0.08)] border-[oklch(0.74_0.16_80_/_0.30)]",
      ring: "shadow-[0_0_0_1px_oklch(0.74_0.16_80_/_0.18),0_4px_16px_oklch(0.55_0.17_190_/_0.05)]",
      mute: "text-[oklch(0.50_0.10_80)]",
      emptyHint: "Nothing in flight.",
    },
    open: {
      band: "bg-muted/40 border-border/60",
      ring: "shadow-[0_0_0_1px_oklch(0.55_0.17_190_/_0.08),0_4px_16px_oklch(0.55_0.17_190_/_0.05)]",
      mute: "text-muted-foreground",
      emptyHint: "No open decisions.",
    },
    resolved: {
      band: "bg-[oklch(0.66_0.16_155_/_0.07)] border-[oklch(0.66_0.16_155_/_0.28)]",
      ring: "shadow-[0_0_0_1px_oklch(0.66_0.16_155_/_0.16),0_4px_16px_oklch(0.55_0.17_190_/_0.05)]",
      mute: "text-[oklch(0.46_0.10_155)]",
      emptyHint: "Nothing locked yet — first decision expected at Thursday's WMS gate.",
    },
    blocked: {
      band: "bg-[oklch(0.62_0.22_25_/_0.07)] border-[oklch(0.62_0.22_25_/_0.28)]",
      ring: "shadow-[0_0_0_1px_oklch(0.62_0.22_25_/_0.16),0_4px_16px_oklch(0.55_0.17_190_/_0.05)]",
      mute: "text-[oklch(0.50_0.18_25)]",
      emptyHint: "No blockers.",
    },
  };

const decisionsSearchSchema = z.object({
  id: z.string().optional(),
});

export const Route = createFileRoute("/_app/decisions/")({
  validateSearch: decisionsSearchSchema,
  component: DecisionsListPage,
});

function DecisionsListPage() {
  const decisionsQ = useDecisionsList();
  const update = useUpdateDecision();
  const navigate = useNavigate({ from: Route.fullPath });
  const { id: openId } = Route.useSearch();
  const decisions = decisionsQ.data ?? [];

  const grouped = STATUS_ORDER.map((status) => ({
    status,
    items: decisions.filter((d) => d.status === status),
  }));

  const handleStatusChange = (id: string, status: Status) => {
    update.mutate({ id, patch: { status } });
  };

  const handleTitleChange = (id: string, current: string, next: string) => {
    const trimmed = next.trim();
    if (trimmed && trimmed !== current) {
      update.mutate({ id, patch: { title: trimmed } });
    }
  };

  const lockedCount = decisions.filter((d) => d.status === "resolved").length;
  const activeCount = decisions.filter((d) => d.status === "active").length;
  const openCount = decisions.filter((d) => d.status === "open").length;
  const blockedCount = decisions.filter((d) => d.status === "blocked").length;

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <RouteHero
        eyebrow="Decision Log"
        icon={GitBranch}
        numeral={String(lockedCount).padStart(2, "0")}
        numeralTone="cyan"
        numeralSuffix={`/ ${String(decisions.length).padStart(2, "0")}`}
        title="Locked, in flight, blocked"
        summary="Each row captures a call we made — or one we still owe. Track the gate it ships against and the path forward."
        meta={[
          { label: "Active", value: activeCount, tone: "amber" },
          { label: "Open", value: openCount },
          { label: "Locked", value: lockedCount, tone: "green" },
          { label: "Blocked", value: blockedCount, tone: "red" },
        ]}
        actions={
          <Link
            to="/decisions/new"
            className="group inline-flex items-center justify-between gap-2 rounded-lg border border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] font-medium text-[oklch(0.93_0.008_240)] transition-all hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)] print:hidden"
          >
            <span className="font-data text-[11px] tracking-[0.12em] uppercase">
              + New decision
            </span>
            <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        }
      />

      <div className="stagger-children flex flex-col gap-4">
        {grouped.map(({ status, items }) => (
          <DecisionGroup
            key={status}
            status={status}
            items={items}
            onStatusChange={handleStatusChange}
            onTitleChange={handleTitleChange}
            onOpen={(id) => navigate({ search: { id } })}
          />
        ))}
      </div>

      <Sheet
        open={!!openId}
        onOpenChange={(open) => {
          if (!open) navigate({ search: {} });
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-2xl">
          <SheetTitle className="sr-only">Decision detail</SheetTitle>
          <SheetDescription className="sr-only">View and edit the full decision.</SheetDescription>
          {openId ? <DecisionDrawerContent id={openId} /> : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DecisionGroup({
  status,
  items,
  onStatusChange,
  onTitleChange,
  onOpen,
}: {
  status: Status;
  items: DecisionRow[];
  onStatusChange: (id: string, status: Status) => void;
  onTitleChange: (id: string, current: string, next: string) => void;
  onOpen: (id: string) => void;
}) {
  const tone = GROUP_TONE[status];
  const isEmpty = items.length === 0;

  return (
    <section
      className={cn("bg-card overflow-hidden rounded-xl", tone.ring, isEmpty && "opacity-60")}
    >
      <header className={cn("flex items-center justify-between border-b px-4 py-2.5", tone.band)}>
        <div className="flex items-center gap-2.5">
          <StatusDot tone={status} size="lg" />
          <span className="font-mono text-[11px] font-medium tracking-[0.16em] uppercase">
            {STATUS_LABEL[status]}
          </span>
        </div>
        <span
          className={cn("font-mono text-[11px] font-medium tracking-wider tabular-nums", tone.mute)}
        >
          {String(items.length).padStart(2, "0")}
        </span>
      </header>

      {isEmpty ? (
        <p className="text-muted-foreground px-4 py-3 text-xs italic">{tone.emptyHint}</p>
      ) : (
        <ul className="flex flex-col">
          {items.map((d, idx) => (
            <li
              key={d.id}
              className={cn(
                "group hover:bg-muted/30 flex items-center gap-2 px-3 py-1.5 transition print:break-inside-avoid",
                idx > 0 && "border-border/40 border-t"
              )}
            >
              <Select value={d.status} onValueChange={(v) => onStatusChange(d.id, v as Status)}>
                <SelectTrigger
                  className="hover:border-border/60 h-7 w-7 justify-center gap-0 border-transparent bg-transparent px-0 [&>svg:last-child]:hidden"
                  aria-label={`Change status from ${STATUS_LABEL[d.status]}`}
                >
                  <StatusDot tone={d.status} size="md" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">
                    <span className="inline-flex items-center gap-2">
                      <StatusDot tone="open" /> Open
                    </span>
                  </SelectItem>
                  <SelectItem value="active">
                    <span className="inline-flex items-center gap-2">
                      <StatusDot tone="active" /> Active
                    </span>
                  </SelectItem>
                  <SelectItem value="resolved">
                    <span className="inline-flex items-center gap-2">
                      <StatusDot tone="resolved" /> Resolved
                    </span>
                  </SelectItem>
                  <SelectItem value="blocked">
                    <span className="inline-flex items-center gap-2">
                      <StatusDot tone="blocked" /> Blocked
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>

              <InlineEditText
                defaultValue={d.title}
                onCommit={(next) => onTitleChange(d.id, d.title, next)}
                className="flex-1"
                ariaLabel="Edit decision title"
              />

              {d.gate ? (
                <span className="border-border/60 text-muted-foreground hidden rounded-sm border px-1.5 py-0.5 font-mono text-[10px] tracking-wider uppercase sm:inline-block">
                  {d.gate}
                </span>
              ) : null}
              <span className="text-muted-foreground w-16 text-right font-mono text-[10px] tabular-nums">
                {d.decided_at ? format(new Date(d.decided_at), "d MMM") : "—"}
              </span>

              <button
                type="button"
                onClick={() => onOpen(d.id)}
                className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-7 shrink-0 items-center justify-center rounded-sm opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100 print:hidden"
                aria-label="Open decision detail"
                title="Open detail"
              >
                <Maximize2 className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function InlineEditText({
  defaultValue,
  onCommit,
  className,
  ariaLabel,
}: {
  defaultValue: string;
  onCommit: (next: string) => void;
  className?: string;
  ariaLabel: string;
}) {
  return (
    <input
      key={defaultValue}
      type="text"
      defaultValue={defaultValue}
      aria-label={ariaLabel}
      className={cn(
        "focus:border-ring focus:bg-background hover:border-border/60 truncate rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-xs transition outline-none",
        className
      )}
      onBlur={(e) => onCommit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          e.currentTarget.value = defaultValue;
          e.currentTarget.blur();
        }
      }}
    />
  );
}
